type FieldValues = number[] | 'every'

interface FieldRange {
  min: number
  max: number
}

const MINUTE_RANGE: FieldRange = { min: 0, max: 59 }
const HOUR_RANGE: FieldRange = { min: 0, max: 23 }
const DAY_OF_MONTH_RANGE: FieldRange = { min: 1, max: 31 }
const MONTH_RANGE: FieldRange = { min: 1, max: 12 }
const DAY_OF_WEEK_RANGE: FieldRange = { min: 0, max: 7 }

const FIELD_COUNT = 5
const SUNDAY = 0
const SUNDAY_ALIAS = 7
const DAYS_IN_WEEK = 7
const MAX_LISTED_TIMES = 6
const MIN_WEEKDAY_RANGE_LENGTH = 3
const WORKDAYS = [1, 2, 3, 4, 5]
const WEEKEND = [0, 6]
const PART_PATTERN = /^(\*|\d+)(?:-(\d+))?(?:\/(\d+))?$/

const WEEKDAY_NAMES = ['söndag', 'måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag']
const MONTH_NAMES = [
  'januari',
  'februari',
  'mars',
  'april',
  'maj',
  'juni',
  'juli',
  'augusti',
  'september',
  'oktober',
  'november',
  'december'
]

const FALLBACK_DESCRIPTION = 'Eget schema'
const EVERY_DAY = 'varje dag'
const EVERY_MONTH = 'varje månad'
const WORKDAYS_LABEL = 'vardagar'
const WEEKEND_LABEL = 'helger'
const EVERY_MINUTE = 'varje minut'
const EVERY_FULL_HOUR = 'varje hel timme'

class UnsupportedScheduleError extends Error {}

function resolvePartEnd(start: number, endText: string | undefined, isOpenEnded: boolean, range: FieldRange): number {
  if (endText !== undefined) {
    return Number(endText)
  }

  return isOpenEnded ? range.max : start
}

function expandPart(part: string, range: FieldRange): number[] {
  const match = PART_PATTERN.exec(part)

  if (!match) {
    throw new UnsupportedScheduleError(part)
  }

  const [, startText, endText, stepText] = match
  const isWildcard = startText === '*'
  const start = isWildcard ? range.min : Number(startText)
  const end = resolvePartEnd(start, endText, isWildcard || stepText !== undefined, range)
  const step = stepText === undefined ? 1 : Number(stepText)
  const isOutOfRange = start < range.min || end > range.max || start > end || step < 1

  if (isOutOfRange) {
    throw new UnsupportedScheduleError(part)
  }

  const values: number[] = []

  for (let value = start; value <= end; value += step) {
    values.push(value)
  }

  return values
}

function expandField(field: string, range: FieldRange, normalize: (value: number) => number = (value) => value): FieldValues {
  if (field === '*') {
    return 'every'
  }

  const values = new Set(field.split(',').flatMap((part) => expandPart(part, range).map(normalize)))
  const possibleValues = new Set(expandPart('*', range).map(normalize))

  if (values.size === possibleValues.size) {
    return 'every'
  }

  return [...values].sort((first, second) => first - second)
}

function normalizeWeekday(value: number): number {
  return value === SUNDAY_ALIAS ? SUNDAY : value
}

function joinList(items: string[]): string {
  if (items.length <= 1) {
    return items.join('')
  }

  return `${items.slice(0, -1).join(', ')} och ${items[items.length - 1]}`
}

function ordinal(value: number): string {
  const lastDigit = value % 10
  const lastTwoDigits = value % 100
  const isFirstOrSecond = (lastDigit === 1 || lastDigit === 2) && lastTwoDigits !== 11 && lastTwoDigits !== 12

  return `${value}:${isFirstOrSecond ? 'a' : 'e'}`
}

function padTime(value: number): string {
  return String(value).padStart(2, '0')
}

function formatTime(hour: number, minute: number): string {
  return `${padTime(hour)}:${padTime(minute)}`
}

function isSameList(first: number[], second: number[]): boolean {
  return first.length === second.length && first.every((value, index) => value === second[index])
}

function isContiguous(values: number[]): boolean {
  return values.every((value, index) => index === 0 || value === values[index - 1]! + 1)
}

function findRegularStep(values: number[], cycleLength: number): number | null {
  if (values.length < 2 || values[0] !== 0) {
    return null
  }

  const step = values[1]! - values[0]!
  const isRegular = values.every((value, index) => value === index * step)

  return isRegular && cycleLength % step === 0 && values.length === cycleLength / step ? step : null
}

function weekdayOrder(day: number): number {
  return (day + DAYS_IN_WEEK - 1) % DAYS_IN_WEEK
}

function describeWeekdays(days: number[]): string {
  if (isSameList(days, WORKDAYS)) {
    return WORKDAYS_LABEL
  }

  if (isSameList(days, WEEKEND)) {
    return WEEKEND_LABEL
  }

  const mondayFirst = [...days].sort((first, second) => weekdayOrder(first) - weekdayOrder(second))
  const names = mondayFirst.map((day) => WEEKDAY_NAMES[day]!)
  const isRange = mondayFirst.length >= MIN_WEEKDAY_RANGE_LENGTH && isContiguous(mondayFirst.map(weekdayOrder))

  if (isRange) {
    return `${names[0]}–${names[names.length - 1]}`
  }

  return `varje ${joinList(names)}`
}

function describeDates(daysOfMonth: number[], monthNames: string | null): string {
  const period = monthNames === null ? EVERY_MONTH : `i ${monthNames}`

  return `den ${joinList(daysOfMonth.map(ordinal))} ${period}`
}

function describeDays(daysOfMonth: FieldValues, months: FieldValues, weekdays: FieldValues): string {
  const monthNames = months === 'every' ? null : joinList(months.map((month) => MONTH_NAMES[month - 1]!))

  if (daysOfMonth === 'every') {
    const days = weekdays === 'every' ? EVERY_DAY : describeWeekdays(weekdays)

    return monthNames === null ? days : `${days} i ${monthNames}`
  }

  const dates = describeDates(daysOfMonth, monthNames)

  return weekdays === 'every' ? dates : `${dates} och ${describeWeekdays(weekdays)}`
}

function describeMinutesPastHour(minutes: number[]): string {
  const unit = minutes.length === 1 && minutes[0] === 1 ? 'minut' : 'minuter'

  return `${joinList(minutes.map(String))} ${unit} över`
}

function describeHourlyTimes(minutes: FieldValues): string {
  if (minutes === 'every') {
    return EVERY_MINUTE
  }

  if (isSameList(minutes, [0])) {
    return EVERY_FULL_HOUR
  }

  const step = findRegularStep(minutes, MINUTE_RANGE.max + 1)

  if (step !== null) {
    return `var ${ordinal(step)} minut`
  }

  return `varje timme, ${describeMinutesPastHour(minutes)}`
}

function describeEveryNthHour(step: number, minute: number): string {
  const minutePart = minute === 0 ? 'på hela timmen' : describeMinutesPastHour([minute])

  return `, var ${ordinal(step)} timme ${minutePart}`
}

function describeDailyTimes(minutes: FieldValues, hours: number[]): string {
  if (minutes === 'every') {
    throw new UnsupportedScheduleError('every minute within hours')
  }

  const times = hours.flatMap((hour) => minutes.map((minute) => formatTime(hour, minute)))

  if (times.length <= MAX_LISTED_TIMES) {
    return ` kl. ${joinList(times)}`
  }

  if (minutes.length !== 1) {
    return `, ${times.length} gånger om dagen`
  }

  const minute = minutes[0]!
  const step = findRegularStep(hours, HOUR_RANGE.max + 1)

  if (step !== null) {
    return describeEveryNthHour(step, minute)
  }

  if (isContiguous(hours)) {
    return `, varje timme mellan ${formatTime(hours[0]!, minute)} och ${formatTime(hours[hours.length - 1]!, minute)}`
  }

  return `, ${times.length} gånger om dagen`
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export function describeSchedule(cron: string): string {
  const fields = cron.trim().split(/\s+/)

  if (fields.length !== FIELD_COUNT) {
    return FALLBACK_DESCRIPTION
  }

  const [minuteField, hourField, dayOfMonthField, monthField, dayOfWeekField] = fields as [string, string, string, string, string]

  try {
    const minutes = expandField(minuteField, MINUTE_RANGE)
    const hours = expandField(hourField, HOUR_RANGE)
    const daysOfMonth = expandField(dayOfMonthField, DAY_OF_MONTH_RANGE)
    const months = expandField(monthField, MONTH_RANGE)
    const weekdays = expandField(dayOfWeekField, DAY_OF_WEEK_RANGE, normalizeWeekday)
    const days = describeDays(daysOfMonth, months, weekdays)

    if (hours === 'every') {
      const times = describeHourlyTimes(minutes)

      return capitalize(days === EVERY_DAY ? times : `${days}, ${times}`)
    }

    return capitalize(`${days}${describeDailyTimes(minutes, hours)}`)
  } catch (error) {
    if (error instanceof UnsupportedScheduleError) {
      return FALLBACK_DESCRIPTION
    }

    throw error
  }
}
